import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { Capacitor } from '@capacitor/core'

// How often an open tab checks whether the deployed build has changed.
// A successful deploy surfaces the toast within this window.
const UPDATE_POLL_MS = 60_000

// Floor between two checks. focus/visibilitychange fire constantly on mobile
// (every tab switch, every address-bar tap), so without this the page would
// re-fetch the shell dozens of times a minute.
const MIN_CHECK_GAP_MS = 30_000

// The build's identity is the content hash Vite puts in the entry bundle's
// filename — it changes if and only if the app's code changed.
//
// Service-worker lifecycle events are NOT a reliable stand-in: a worker can
// install and activate again without the app having changed, and every one of
// those used to fire the toast (the 2026-08-17 report).
const ENTRY_RE = /assets\/index-[A-Za-z0-9_-]+\.js/

function entryBundleIn(text) {
  return text.match(ENTRY_RE)?.[0] ?? null
}

// The entry bundle this document actually loaded. null in dev (unhashed
// module graph) and under Capacitor, where the check is skipped anyway.
function runningEntryBundle() {
  const el = document.querySelector('script[type="module"][src*="/assets/index-"]')
  return entryBundleIn(el?.getAttribute('src') ?? '')
}

// The entry bundle a reload of *this page* would run.
//
// It matters that this asks for the current URL rather than /index.html: the
// prompt's only honest claim is "reloading gets you something newer", and that
// depends on what the caches in front of this URL would hand back. Render's
// SPA rewrite serves the same shell for every route, but the CDN caches it per
// path (s-maxage=300 on rewrites, no-store only on the literal /index.html), so
// asking a different URL than the one the user is on can report a build that
// reloading would not actually deliver — which is a prompt the user cannot get
// rid of. Don't add a cache-busting query param for the same reason.
async function reloadableEntryBundle() {
  const res = await fetch(location.pathname + location.search, { cache: 'no-store' })
  if (!res.ok) return null
  return entryBundleIn(await res.text())
}

export function AppUpdate() {
  // Service workers don't run inside Capacitor's native WebView; updates
  // on iOS/Android go through the app stores, not this flow.
  const isNative = Capacitor.isNativePlatform()

  // The build we've already prompted for. Keyed by bundle name so two deploys
  // in a row each get a prompt, and so a dismissed prompt never comes back for
  // the same build.
  const promptedForRef = useRef(null)
  const lastCheckRef = useRef(0)
  const registrationRef = useRef(null)
  const [reloading, setReloading] = useState(false)

  // The worker is registered by hand rather than through vite-plugin-pwa's
  // `virtual:pwa-register` helper. In 'prompt' mode that helper attaches a
  // 'controlling' listener that calls window.location.reload() as soon as a
  // worker it did not start itself takes over — an update from another tab,
  // the browser's own check on navigation, or the registration.update() below
  // all count. Paired with skipWaiting, the new worker takes over within
  // seconds, so open tabs reloaded themselves with no warning: the forced
  // navigation this app deliberately avoids, and a good way to throw away
  // picks someone is halfway through making. Registering directly keeps every
  // reload in this file, behind the button.
  useEffect(() => {
    if (isNative || !import.meta.env.PROD || !('serviceWorker' in navigator)) return
    let cancelled = false
    navigator.serviceWorker.register('/sw.js', { scope: '/' })
      .then((registration) => { if (!cancelled) registrationRef.current = registration })
      .catch(() => { /* an unregistrable worker just means no offline support */ })
    return () => { cancelled = true }
  }, [isNative])

  // The check is deliberately independent of the service worker: it is a plain
  // HTTP comparison, so it also works on the loads where no worker controls the
  // page yet (first visit, evicted storage, browsers with SW disabled).
  useEffect(() => {
    if (isNative) return
    const running = runningEntryBundle()
    // No hashed entry bundle means a dev build — nothing to compare against.
    if (!running) return

    let cancelled = false

    const check = async () => {
      const now = Date.now()
      if (cancelled || now - lastCheckRef.current < MIN_CHECK_GAP_MS) return
      lastCheckRef.current = now

      // Refresh the worker in the background so its precached shell stays a
      // recent one for offline loads. Independent of the prompt: whether it
      // installs anything is not evidence that the app changed.
      registrationRef.current?.update().catch(() => { /* network blips are fine */ })

      let reloadable
      try {
        reloadable = await reloadableEntryBundle()
      } catch {
        return // offline, or the deploy is mid-swap — try again next tick
      }
      if (cancelled || !reloadable || reloadable === running) return
      if (promptedForRef.current === reloadable) return
      promptedForRef.current = reloadable

      showUpdateToast(setReloading)
    }

    const onVisibilityChange = () => { if (!document.hidden) check() }
    const timer = window.setInterval(check, UPDATE_POLL_MS)
    window.addEventListener('focus', check)
    document.addEventListener('visibilitychange', onVisibilityChange)

    // One check on load, for the tab that was restored from a days-old session.
    check()

    return () => {
      cancelled = true
      window.clearInterval(timer)
      window.removeEventListener('focus', check)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [isNative])

  if (!reloading) return null

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-background/90 backdrop-blur-sm"
      role="status"
      aria-live="polite"
    >
      <div className="flex flex-col items-center gap-3">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-primary" />
        <p className="text-sm font-medium text-foreground">Reloading…</p>
      </div>
    </div>
  )
}

function showUpdateToast(setReloading) {
  toast('New version available', {
    description: 'Reload to get the latest changes.',
    duration: Infinity,
    action: {
      label: 'Reload',
      onClick: () => {
        setReloading(true)
        toast.dismiss('app-update')
        // A plain reload is enough, and waiting for the service worker is not:
        // navigations are served network-first (see vite.config.js), so the
        // fresh shell comes off the network and its hashed assets — which the
        // old precache doesn't have — fall through to the network too. The
        // timeout only gives the overlay a turn to render first.
        window.setTimeout(() => window.location.reload(), 0)
      },
    },
    id: 'app-update',
  })
}
